import { Mutation } from '@apollo/client/react/components';
import * as React from 'react';

import OrgRoleSelection from 'components/OrgRoleSelection';
import Dialog from 'components/Dialog';
import Text from 'components/Text';
import BoxFooter from 'components/BoxFooter';
import Row from 'components/Row';
import Button from 'components/Button';
import { OrganizationRole } from 'globalTypes';
import Link from 'components/Link';
import ADMIN_CHANGE_ROLES_FOR_USERS_MUTATION from 'users/queries/AdminChangeRolesForUsersMutation.gql';
import {
  adminChangeRolesForUsers,
  adminChangeRolesForUsersVariables,
} from 'users/queries/__generated__/adminChangeRolesForUsers';
import ToastActions from 'actions/ToastActions';
import LinkConstants from 'constants/LinkConstants';
import { isFeatureEnabled } from 'lib/FeatureFlag';

export type Props = {
  // Subset of PeopleListRow
  rows: {
    roles: OrganizationRole[];
    user: { firstName: string; lastName: string; uuid: string };
  }[];
  organizationUuid: string;
  onRequestHide: () => void;
};

type State = {
  selectedRoles: OrganizationRole[];
  noRoleSelected: boolean;
  sameExistingRolesForAll: boolean;
};

const equivalentRoleArrays = (roles1: OrganizationRole[], roles2: OrganizationRole[]) => {
  return roles1.length === roles2.length && roles1.every((value) => roles2.includes(value));
};
export default class BulkRoleChangeModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { rows } = props;
    const isSingleUser = props.rows.length === 1;
    const sameExistingRolesForAll = rows.reduce(
      (acc, row) => equivalentRoleArrays(rows[0].roles, row.roles) && acc,
      true
    );

    this.state = {
      selectedRoles: isSingleUser ? rows[0].roles : [],
      noRoleSelected: isSingleUser ? rows[0].roles.length === 0 : false,
      sameExistingRolesForAll,
    };
  }

  disableChangeRoleButton = () => {
    const { noRoleSelected, selectedRoles, sameExistingRolesForAll } = this.state;
    const { rows } = this.props;

    const sameAsChosen = equivalentRoleArrays(rows[0].roles, selectedRoles);

    return (
      (selectedRoles.length === 0 && !noRoleSelected) || (sameExistingRolesForAll && sameAsChosen)
    );
  };

  render() {
    const { rows, organizationUuid, onRequestHide } = this.props;
    const { selectedRoles, noRoleSelected } = this.state;

    const availableRoles = [
      OrganizationRole.student,
      OrganizationRole.parent,
      OrganizationRole.teacher,
      OrganizationRole.staff,
    ];

    if (isFeatureEnabled('direct-add-employee-role.web.nov-23'))
      availableRoles.push(OrganizationRole.employee);

    return (
      <Dialog
        onRequestHide={onRequestHide}
        size="medium"
        title="Change role"
        subtext={
          <Text size="xxsmall">
            {rows.length === 1 ? (
              <div data-test="single-user-description">
                Change the role of {rows[0].user.firstName} {rows[0].user.lastName}? They will be
                notified.
              </div>
            ) : (
              <div data-test="multi-user-description">
                Change the role of {rows.length} people? They will be notified. People with multiple
                roles will just have the new role{selectedRoles.length > 1 ? 's' : ''} selected.{' '}
                <Link href={LinkConstants.ADMIN_ROLE_CHANGE} target="_blank">
                  Learn more
                </Link>
              </div>
            )}
          </Text>
        }
      >
        <BoxFooter>
          <OrgRoleSelection
            selectedRoles={selectedRoles}
            availableRoles={availableRoles}
            onChange={(newSelectedRoles, noRoleSelected) =>
              this.setState({ selectedRoles: newSelectedRoles, noRoleSelected })
            }
            showNoRole
            noRoleSelected={noRoleSelected}
          />
          <Row size="medium" />
          <Mutation<adminChangeRolesForUsers, adminChangeRolesForUsersVariables>
            mutation={ADMIN_CHANGE_ROLES_FOR_USERS_MUTATION}
            onCompleted={(data) => {
              if (data.api_adminChangeRolesForUsers?.success) {
                ToastActions.showSuccessToast(
                  `${rows.length} roles changed. Refresh the page to see the new roles.`
                );
              } else {
                const errors = data.api_adminChangeRolesForUsers?.errors;
                if (errors && errors.length) {
                  console.error(
                    `BulkRoleChangeModal — role change failed: ${errors[0].code} — ${errors[0].message}`
                  );
                  ToastActions.showErrorToast(errors[0].message);
                } else {
                  console.error('BulkRoleChangeModal — role change failed: unknown error');
                  ToastActions.showDefaultErrorToast();
                }
              }
              onRequestHide();
            }}
            onError={(error) => {
              console.error(`BulkRoleChangeModal — role change failed: ${error.message}`);
              ToastActions.showErrorToast(error.message);
              onRequestHide();
            }}
          >
            {(changeRoles: (input: { variables: adminChangeRolesForUsersVariables }) => void) => {
              return (
                <Button
                  data-test="save-button"
                  data-testid="change-role-button"
                  full
                  color="mascot"
                  disabled={this.disableChangeRoleButton()}
                  onClick={() =>
                    changeRoles({
                      variables: {
                        input: {
                          organizationUuid,
                          userUuids: rows
                            .filter((row) => !equivalentRoleArrays(row.roles, selectedRoles))
                            .map((row) => row.user.uuid),
                          roles: selectedRoles,
                        },
                      },
                    })
                  }
                >
                  Change role
                </Button>
              );
            }}
          </Mutation>
          <Row size="xsmall" />
          <Button level="primary" onClick={onRequestHide}>
            Cancel
          </Button>
        </BoxFooter>
      </Dialog>
    );
  }
}
